"use client";

import { withRecaptcha } from "@/utils/recaptcha";
import { RECAPTCHA_ERROR } from "@/lib/recaptcha";
import { trackOpenaiAcquisitionLeadCreated } from "@/lib/analytics/openaiPixel";
import Link from "next/link";
import React, { useCallback, useTransition } from "react";
import { useActionState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { fieldFromTarget } from "@/lib/analytics/formFields";
import type { FormFlow } from "@/lib/analytics/formFlow";
import { useLeadSource } from "@/hooks/useLeadSource";
import { getVisibleSteps } from "../lib/form-config";
import { submitAppraisal, updateFormStep } from "../actions";
import type { VehicleInfo } from "../lib/types";
import { ConversationalContainer } from "./ConversationalContainer";
interface FormStepperProps {
  vehicleInfo: VehicleInfo;
  flow: FormFlow;
}
export function FormStepper({
  vehicleInfo,
  flow
}: FormStepperProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialCtaSource = searchParams.get("cta_source") || "";
  const initialBuyingCenter = searchParams.get("buying_center") || searchParams.get("location") || "";

  // Initialize form state with vehicle data
  const initialStepState = {
    success: true,
    currentStep: 0,
    appraisalData: {
      vin: vehicleInfo.vin,
      year: vehicleInfo.year,
      make: vehicleInfo.make,
      model: vehicleInfo.model,
      trim: vehicleInfo.trim || "",
      bodyStyle: vehicleInfo.bodyStyle || "",
      engine: vehicleInfo.engine || "",
      driveline: vehicleInfo.driveline || "",
      transmission: vehicleInfo.transmission || "",
      licensePlate: vehicleInfo.licensePlate || "",
      licenseState: vehicleInfo.licenseState || "",
      mileage: "",
      modifications: "",
      modificationsDescription: "",
      condition: "",
      startsRunsDrives: "",
      sellOrTrade: "",
      accidents: "",
      accidentDetails: "",
      comments: "",
      numberOfKeys: 2,
      firstOwner: false,
      name: "",
      email: "",
      phone: "",
      postalCode: "",
      vendor: "THIScar.com - Sell My Car",
      ctaSource: initialCtaSource,
      buyingCenter: initialBuyingCenter
    }
  };
  const [stepState, stepAction, isStepPending] = useActionState(updateFormStep, initialStepState);
  const [submitState, formAction, isSubmitting] = useActionState(withRecaptcha("sell_appraisal", submitAppraisal, () => ({
    success: false,
    errorCode: "verification" as const,
    errors: {
      general: RECAPTCHA_ERROR
    }
  })), null);
  const [isPending, startTransition] = useTransition();
  const {
    leadSource
  } = useLeadSource();

  // Server actions handle all state and error management automatically

  // Get visible steps based on current data
  const visibleSteps = React.useMemo(() => getVisibleSteps(stepState.appraisalData), [stepState.appraisalData]);
  const currentStepConfig = visibleSteps[stepState.currentStep];
  const isLastStep = stepState.currentStep === visibleSteps.length - 1;
  const previousStep = React.useRef<{
    id: string;
    index: number;
  } | null>(null);
  React.useEffect(() => {
    flow.configureSteps(["vehicle_lookup", ...visibleSteps.map(step => step.id)]);
    if (!currentStepConfig) return;
    const previous = previousStep.current;
    if (previous && stepState.success && stepState.currentStep > previous.index) flow.complete(previous.id);
    previousStep.current = {
      id: currentStepConfig.id,
      index: stepState.currentStep
    };
    flow.view(currentStepConfig.id);
    if (!stepState.success) flow.error("validation", currentStepConfig.id);
  }, [stepState, currentStepConfig, visibleSteps, flow]);
  React.useEffect(() => {
    if (submitState?.success && submitState.redirect) {
      if (submitState.outcome === "accepted") trackOpenaiAcquisitionLeadCreated(submitState.conversionEventId);
      flow.finish(submitState.analyticsAttemptId, "contact", submitState.outcome ?? "accepted");
      if (["/sell-my-car/thank-you", "/sell-my-car/unable-to-purchase"].includes(submitState.redirect)) {
        router.push(submitState.redirect);
      }
    }
  }, [submitState, flow, router]);
  const handleStepUpdate = useCallback((updates: Partial<typeof stepState.appraisalData>) => {
    if (!currentStepConfig) {
      return;
    }
    flow.begin(currentStepConfig.id);
    const formData = new FormData();
    formData.append("action", "update");
    formData.append("currentStep", stepState.currentStep.toString());
    formData.append("stepId", currentStepConfig.id);
    formData.append("stepData", JSON.stringify(updates));
    startTransition(() => {
      stepAction(formData);
    });
  }, [currentStepConfig, stepState, stepAction, startTransition, flow]);
  const flushPendingUpdates = useCallback(async () => {
    // With useActionState, actions are handled automatically - no need to track promises
    // This is kept for compatibility but doesn't need to do anything
    return Promise.resolve();
  }, []);

  // Real-time validation function for form components
  const handleValidateStep = useCallback((updates_0: Partial<typeof stepState.appraisalData>) => {
    if (!currentStepConfig) return;
    startTransition(() => {
      const formData_0 = new FormData();
      formData_0.append("action", "validate");
      formData_0.append("currentStep", stepState.currentStep.toString());
      formData_0.append("stepId", currentStepConfig.id);
      formData_0.append("stepData", JSON.stringify(updates_0));
      stepAction(formData_0);
    });
  }, [stepState, currentStepConfig, stepAction, startTransition]);
  const handleNext = useCallback(async (updates_1?: Partial<typeof stepState.appraisalData>) => {
    if (stepState.currentStep < visibleSteps.length - 1 && currentStepConfig) {
      await flushPendingUpdates();
      startTransition(() => {
        const formData_1 = new FormData();
        formData_1.append("action", "next");
        formData_1.append("currentStep", stepState.currentStep.toString());
        formData_1.append("stepId", currentStepConfig.id);
        formData_1.append("stepData", JSON.stringify(updates_1 ?? {}));
        stepAction(formData_1);
      });
    }
  }, [stepState, visibleSteps.length, currentStepConfig, stepAction, startTransition, flushPendingUpdates]);
  const handlePrevious = useCallback(async () => {
    if (stepState.currentStep > 0) {
      await flushPendingUpdates();
      startTransition(() => {
        const formData_2 = new FormData();
        formData_2.append("action", "previous");
        formData_2.append("currentStep", stepState.currentStep.toString());
        formData_2.append("stepData", JSON.stringify({}));
        stepAction(formData_2);
      });
    }
  }, [stepState, stepAction, startTransition, flushPendingUpdates]);

  // For the final form submission, we need a callback that can be called directly
  const finalSubmitStartedRef = React.useRef(false);
  const submissionIdentity = React.useRef<{
    payload: string;
    id: string;
  } | null>(null);
  React.useEffect(() => {
    if (submitState?.errors && Object.keys(submitState.errors).length > 0) {
      finalSubmitStartedRef.current = false;
      flow.error(submitState.errorCode ?? "request_failed", "contact");
      if (submitState.pendingReview) flow.finish(submitState.analyticsAttemptId, "contact", "pending_review");
    }
  }, [submitState, flow]);
  const handleFinalSubmitCallback = useCallback(async (overrides?: Partial<typeof stepState.appraisalData>) => {
    if (submitState?.pendingReview || finalSubmitStartedRef.current || isSubmitting || isPending) {
      return;
    }
    finalSubmitStartedRef.current = true;
    flow.submit("contact");
    await flushPendingUpdates();
    startTransition(() => {
      const formData_3 = new FormData();
      flow.attach(formData_3);
      formData_3.set("responseMode", "state");

      // Add all appraisal data
      const aggregatedData = {
        ...stepState.appraisalData,
        ...(overrides || {})
      };
      const payloadIdentity = JSON.stringify(Object.entries(aggregatedData).sort(([a], [b]) => a.localeCompare(b)));
      if (submissionIdentity.current?.payload !== payloadIdentity) {
        submissionIdentity.current = {
          payload: payloadIdentity,
          id: crypto.randomUUID()
        };
      }
      formData_3.set("submissionId", submissionIdentity.current.id);
      Object.entries(aggregatedData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData_3.append(key, value.toString());
        }
      });

      // Add UTM data from lead source (send empty strings if not present - API expects all fields)
      formData_3.set("utmSource", leadSource?.source || "");
      formData_3.set("utmCampaign", leadSource?.campaign || "");
      formData_3.set("utmMedium", leadSource?.medium || "");
      formData_3.set("utmTerm", leadSource?.term || "");
      formData_3.set("utmContent", leadSource?.content || "");
      formData_3.set("ctaSource", stepState.appraisalData.ctaSource || searchParams.get("cta_source") || "");
      formData_3.set("buyingCenter", stepState.appraisalData.buyingCenter || initialBuyingCenter);

      // Add vendor from search params or default
      let vendor = searchParams.get("vendor") || "THIScar.com - Sell My Car";

      // Override vendor to "carpro" if UTM source is or includes "carpro"
      if (leadSource?.source && leadSource.source.toLowerCase().includes("carpro")) {
        vendor = "carpro";
      }
      formData_3.set("vendor", vendor);
      const productInterest = ["Appraisal", aggregatedData.year, aggregatedData.make, aggregatedData.model, aggregatedData.trim, aggregatedData.vin ? `VIN ${aggregatedData.vin}` : ""].filter(Boolean).join(" ");
      const submissionId = crypto.randomUUID();
      formData_3.set("kisoSubmissionId", submissionId);
      const kisoPayload = {
        email: aggregatedData.email,
        phone: aggregatedData.phone,
        fullName: aggregatedData.name,
        firstName: aggregatedData.name?.split(" ")[0],
        lastName: aggregatedData.name?.split(" ").slice(1).join(" "),
        productInterest,
        message: aggregatedData.comments,
        formUrl: window.location.href,
        vendor,
        submissionId
      };
      const kiso = window as Window & {
        Kiso?: {
          captureFormSubmission?: (payload: Record<string, unknown>, options?: Record<string, unknown>) => Promise<boolean>;
        };
      };
      try {
        void Promise.resolve(kiso.Kiso?.captureFormSubmission?.(kisoPayload, {
          formId: "sell-my-car-appraisal",
          captureMethod: "kiso_form",
          submissionId
        })).catch(error_0 => {
          console.error("Error capturing form submission with Kiso:", error_0);
        });
      } catch (error) {
        console.error("Error capturing form submission with Kiso:", error);
      }
      formAction(formData_3);
    });
  }, [stepState, leadSource, searchParams, initialBuyingCenter, formAction, startTransition, flushPendingUpdates, isSubmitting, isPending, flow, submitState?.pendingReview]);
  if (submitState?.pendingReview) {
    return <section className="t-mx-auto t-max-w-xl t-rounded-2xl t-bg-white t-p-8 t-shadow-lg" role="status">
            <h2 className="t-text-2xl t-font-bold">Your request is saved</h2>
            <p className="t-mt-4">We need to review its delivery or an existing offer before confirming a new appraisal. Please do not submit it again.</p>
            <p className="t-mt-4">For help, <Link className="t-font-bold t-underline" href="/support">contact THIScar</Link>.</p>
            <p className="t-mt-4 t-break-all t-text-xs">Reference: {submitState.submissionId}</p>
        </section>;
  }
  if (!currentStepConfig) {
    return <div className="t-min-h-screen t-bg-ecommerce-background t-flex t-items-center t-justify-center">
                <div className="t-text-center t-space-y-4">
                    <div className="t-animate-spin t-rounded-full t-h-12 t-w-12 t-border-b-2 t-border-automotive-primary t-mx-auto"></div>
                    <div className="t-text-automotive-primary t-font-medium">Loading your appraisal form...</div>
                </div>
            </div>;
  }
  const StepComponent = currentStepConfig.component;
  return <ConversationalContainer currentStep={stepState.currentStep} totalSteps={visibleSteps.length} onNext={isLastStep ? (overrides_0?: Partial<typeof stepState.appraisalData>) => handleFinalSubmitCallback(overrides_0) : (updates_2?: Partial<typeof stepState.appraisalData>) => handleNext(updates_2)} onPrevious={stepState.currentStep > 0 ? handlePrevious : undefined} vehicleInfo={{
    year: typeof stepState.appraisalData.year === "string" ? parseInt(stepState.appraisalData.year, 10) : stepState.appraisalData.year,
    make: stepState.appraisalData.make,
    model: stepState.appraisalData.model
  }}>
            <div className="t-w-full" onFocusCapture={event => {
      if (currentStepConfig.id === "contact") flow.field(fieldFromTarget(event.target));
    }} onInputCapture={event_0 => {
      if (currentStepConfig.id === "contact") flow.field(fieldFromTarget(event_0.target));
    }}>
                <StepComponent data={stepState.appraisalData} onFieldError={field => flow.fieldError(field)} onUpdate={handleStepUpdate} onValidate={handleValidateStep} onNext={isLastStep ? handleFinalSubmitCallback : handleNext} onPrevious={stepState.currentStep > 0 ? handlePrevious : undefined} errors={{
        ...stepState.errors,
        ...submitState?.errors
      }} isLoading={isStepPending || isSubmitting || isPending} currentStepConfig={currentStepConfig} stepNumber={stepState.currentStep + 1} totalSteps={visibleSteps.length} {...isLastStep && {
        onSubmit: (overrides_1?: Partial<typeof stepState.appraisalData>) => handleFinalSubmitCallback(overrides_1),
        isSubmitting: isSubmitting
      }} />

                {/* Submission Error Display */}
                {submitState?.errors && Object.keys(submitState.errors).length > 0 && <div className="t-mt-6 t-p-4 t-bg-brand-rose-soft t-border t-border-brand-rose-soft t-rounded-lg">
                        <div className="t-flex t-items-start t-space-x-3">
                            <svg className="t-h-6 t-w-6 t-text-brand-rose t-flex-shrink-0 t-mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="t-flex-1">
                                <h3 className="t-text-brand-rose-dark t-font-medium t-text-lg">Submission Failed</h3>
                                <div className="t-mt-2 t-text-brand-rose-dark">
                                    {Object.entries(submitState.errors).map(([field_0, message]) => <p key={field_0} className="t-text-sm">
                                            {field_0 === "general" ? message : `${field_0}: ${message}`}
                                        </p>)}
                                </div>
                                <div className="t-mt-4">
                                    <button onClick={() => void handleFinalSubmitCallback()} disabled={isSubmitting || isPending} className="t-inline-flex t-items-center t-px-4 t-py-2 t-bg-brand-rose t-text-white t-text-sm t-font-medium t-rounded-md hover:t-bg-brand-rose-dark disabled:t-opacity-50 disabled:t-cursor-not-allowed">
                                        {isSubmitting || isPending ? <>
                                                <svg className="t-animate-spin t-mr-2 t-h-4 t-w-4" fill="none" viewBox="0 0 24 24">
                                                    <circle className="t-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="t-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Retrying...
                                            </> : "Try Again"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>}


            </div>
        </ConversationalContainer>;
}